import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import "./SchoolVehicles.css";

const initialForm = {
  name: "",
  vehicle_type: "",
  brand: "",
  model: "",
  transmission: "",
  fuel_type: "",
  seating_capacity: "",
  has_female_driver: false,
  is_active: true,
  notes: "",
};

const SchoolVehicles = () => {
  const { bearerAuth } = useApi();
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadVehicles = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await bearerAuth("driving-school/vehicles");
      setVehicles(res?.data?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load vehicles.");
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEdit = (vehicle) => {
    setEditingId(vehicle.id);
    setForm({
      name: vehicle.name || "",
      vehicle_type: vehicle.vehicle_type || "",
      brand: vehicle.brand || "",
      model: vehicle.model || "",
      transmission: vehicle.transmission || "",
      fuel_type: vehicle.fuel_type || "",
      seating_capacity: vehicle.seating_capacity || "",
      has_female_driver: !!vehicle.has_female_driver,
      is_active: !!vehicle.is_active,
      notes: vehicle.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...form,
        seating_capacity: form.seating_capacity ? Number(form.seating_capacity) : null,
      };

      const endpoint = editingId
        ? `driving-school/vehicles/${editingId}`
        : "driving-school/vehicles";

      await bearerAuth(endpoint, "POST", payload);
      resetForm();
      loadVehicles();
    } catch (err) {
      setError(err?.message || "Failed to save vehicle.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Delete this vehicle from your fleet?")) {
      return;
    }

    try {
      await bearerAuth(`driving-school/vehicles/${vehicleId}/delete`, "POST");
      if (editingId === vehicleId) {
        resetForm();
      }
      loadVehicles();
    } catch (err) {
      setError(err?.message || "Failed to delete vehicle.");
    }
  };

  // Mobile card view for vehicles
  const MobileVehicleCard = ({ vehicle }) => (
    <div className="school-vehicle-mobile-card">
      <div className="vehicle-header">
        <div>
          <div className="vehicle-name">{vehicle.name}</div>
          <div className="vehicle-model">
            {[vehicle.brand, vehicle.model].filter(Boolean).join(" ")}
          </div>
        </div>
        <div className={`vehicle-status-badge ${vehicle.is_active ? 'active' : 'inactive'}`}>
          {vehicle.is_active ? 'Active' : 'Inactive'}
        </div>
      </div>

      <div className="vehicle-details-grid">
        <div className="detail-item">
          <span className="detail-label">Type</span>
          <span className="detail-value">
            {vehicle.vehicle_type || vehicle.transmission || "Standard"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Driver</span>
          <span className={`detail-value ${vehicle.has_female_driver ? 'female-driver' : ''}`}>
            {vehicle.has_female_driver ? '👩 Female Driver' : '👨 General'}
          </span>
        </div>

        {vehicle.seating_capacity && (
          <div className="detail-item">
            <span className="detail-label">Seats</span>
            <span className="detail-value">{vehicle.seating_capacity}</span>
          </div>
        )}

        {vehicle.fuel_type && (
          <div className="detail-item">
            <span className="detail-label">Fuel</span>
            <span className="detail-value">{vehicle.fuel_type}</span>
          </div>
        )}
      </div>

      {vehicle.notes && (
        <div className="vehicle-notes">
          <span className="notes-label">Notes:</span>
          <p className="notes-text">{vehicle.notes}</p>
        </div>
      )}

      <div className="vehicle-actions">
        <button
          type="button"
          className="btn-edit"
          onClick={() => handleEdit(vehicle)}
        >
          Edit
        </button>
        <button
          type="button"
          className="btn-delete"
          onClick={() => handleDelete(vehicle.id)}
        >
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className="school-vehicles-container">
      <div className="vehicles-layout">
        {/* Form Section */}
        <div className="form-section">
          <div className="form-card">
            <div className="form-header">
              <h5 className="form-title">{editingId ? "Edit Vehicle" : "Add Vehicle"}</h5>
              {editingId && (
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>

            <p className="form-description">
              Keep your fleet updated so students can choose schools based on car type, availability, and female-driver support.
            </p>

            <form onSubmit={handleSubmit} className="vehicle-form">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label">Display Name</label>
                  <input
                    className="form-input"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Swift Dzire - Training Car 1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Vehicle Type</label>
                  <select className="form-select" name="vehicle_type" value={form.vehicle_type} onChange={handleChange}>
                    <option value="">Select type</option>
                    <option value="hatchback">Hatchback</option>
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <input className="form-input" name="brand" value={form.brand} onChange={handleChange} placeholder="Maruti" />
                </div>

                <div className="form-group">
                  <label className="form-label">Model</label>
                  <input className="form-input" name="model" value={form.model} onChange={handleChange} placeholder="Dzire" />
                </div>

                <div className="form-group">
                  <label className="form-label">Seating Capacity</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    className="form-input"
                    name="seating_capacity"
                    value={form.seating_capacity}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Transmission</label>
                  <select className="form-select" name="transmission" value={form.transmission} onChange={handleChange}>
                    <option value="">Select transmission</option>
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Fuel Type</label>
                  <select className="form-select" name="fuel_type" value={form.fuel_type} onChange={handleChange}>
                    <option value="">Select fuel</option>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="cng">CNG</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_female_driver"
                        checked={form.has_female_driver}
                        onChange={handleChange}
                      />
                      <span>Female driver available for this vehicle</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={form.is_active}
                        onChange={handleChange}
                      />
                      <span>Active and bookable</span>
                    </label>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-textarea"
                    rows="3"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Special notes, learner-friendly features, pickup support, etc."
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update Vehicle" : "Add Vehicle"}
              </button>
            </form>
          </div>
        </div>

        {/* Fleet List Section */}
        <div className="fleet-section">
          <div className="fleet-card">
            <div className="fleet-header">
              <h5 className="fleet-title">Fleet Overview</h5>
              <span className="vehicle-count">{vehicles.length} vehicles</span>
            </div>

            {error && <div className="alert-error">{error}</div>}

            {loading ? (
              <div className="loading-state">Loading vehicles...</div>
            ) : vehicles.length === 0 ? (
              <div className="empty-state">
                No vehicles added yet. Add your first training car to start matching student preferences.
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="desktop-table">
                  <table className="vehicles-table">
                    <thead>
                      <tr>
                        <th>Vehicle</th>
                        <th>Type</th>
                        <th>Driver</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map((vehicle) => (
                        <tr key={vehicle.id}>
                          <td>
                            <div className="vehicle-info">
                              <div className="vehicle-name">{vehicle.name}</div>
                              <div className="vehicle-details">
                                {[vehicle.brand, vehicle.model].filter(Boolean).join(" ")}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge-type">
                              {vehicle.vehicle_type || vehicle.transmission || "Standard"}
                            </span>
                          </td>
                          <td>
                            <span className={`badge-driver ${vehicle.has_female_driver ? 'female' : 'general'}`}>
                              {vehicle.has_female_driver ? 'Female driver' : 'General'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge-status ${vehicle.is_active ? 'active' : 'inactive'}`}>
                              {vehicle.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="btn-action edit"
                              onClick={() => handleEdit(vehicle)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn-action delete"
                              onClick={() => handleDelete(vehicle.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-cards">
                  {vehicles.map((vehicle) => (
                    <MobileVehicleCard key={vehicle.id} vehicle={vehicle} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolVehicles;